import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import ADD from '../assets/add.svg'
import SEARCH from '../assets/search.svg'
import AddQuestionModal from './AddQuestionModal'
import { selectSelectedTask } from '../../../slice/taskSlice';
import { createStandardQuestion, fetchSQByTaskId, selectSQ, selectSQLoading, selectSQError, deleteStandardQuestion } from '../../../slice/sqSlice';
import { Trash2Icon, Loader2 } from 'lucide-react';
import constants from '../../../components/constants';
import NotFoundErro from './atomic/NotFoundErro';
import DeleteModal from '../../../components/atomic/DeleteModal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { reorderSQ } from '../../../Api/sqApi'; // You will create this API call
import Loader from '../../../components/atomic/Loader';

const StandarQustions = () => {

  const { data } = useSelector((state) => state.admin);

  const isAdmin =
    data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const userOrganizationId = data?.organization_id;
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  
  const selectedTask = useSelector(selectSelectedTask);
  const questions = useSelector(selectSQ);
  const loading = useSelector(selectSQLoading);
  const error = useSelector(selectSQError);

  // Fetch questions whenever selectedTask changes
  useEffect(() => {
    if (selectedTask?.id) {
      console.log('Fetching questions for task:', selectedTask.id);
      dispatch(fetchSQByTaskId(selectedTask.id));
    }
  }, [selectedTask?.id, dispatch]);

  const handleAddQuestion = async (questionData) => {
    if (!selectedTask?.id) {
      console.error('No task selected');
      return;
    }

    // Check permissions for ADMIN users
    if (isAdmin && !isSuperAdmin) {
      // ADMIN users can only add questions to tasks assigned to their organization
      if (selectedTask.organization_id && selectedTask.organization_id !== userOrganizationId) {
        console.error('ADMIN users can only add questions to tasks assigned to their organization');
        return;
      }
    }

    try {
      await dispatch(createStandardQuestion({
        ...questionData,
        task_id: selectedTask.id
      })).unwrap();
        dispatch(fetchSQByTaskId(selectedTask.id));
      
      setIsModalOpen(false);
      // Refetch questions after creating new one
    
    } catch (err) {
      console.error('Failed to create question:', err);
    }
  };

  const handleDelete = (e, id) => {
    e.stopPropagation(); // Prevent the click from bubbling up to the parent
    
    // Check permissions for ADMIN users
    if (isAdmin && !isSuperAdmin) {
      // ADMIN users can only delete questions from tasks assigned to their organization
      if (selectedTask.organization_id && selectedTask.organization_id !== userOrganizationId) {
        console.error('ADMIN users can only delete questions from tasks assigned to their organization');
        return;
      }
    }
    
    setQuestionToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteStandardQuestion(questionToDelete)).unwrap();
      // addToast('Standard question deleted successfully', 'success');
    } catch (error) {
      // addToast(error.message || 'Failed to delete standard question', 'error');
    }
    setIsDeleteModalOpen(false);
    setQuestionToDelete(null);
  };

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedQuestion(null);
    setIsModalOpen(false);
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const filteredQuestions = questions.filter(question => 
    question?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    question?.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return; // No move

    // Check if order actually changes
    const currentOrder = questions.map(q => q.id);
    const reordered = Array.from(questions);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    const newOrder = reordered.map(q => q.id);
    const isSameOrder = currentOrder.every((id, idx) => id === newOrder[idx]);
    if (isSameOrder) return;

    setReorderLoading(true);
    // Update order in backend
    const orderList = reordered.map((q, idx) => ({ id: q.id, order: idx + 1 }));
    await reorderSQ(orderList);
    await dispatch(fetchSQByTaskId(selectedTask.id));
    setReorderLoading(false);
  };

  if (!selectedTask?.id) {
    return (
      <div className="py-4 text-center text-gray-500">
        Please select a task to view its questions
      </div>
    );
  }

  return (
    <div>
      <div className='flex justify-between items-center gap-10 mb-6'>
          <div className='relative w-full'>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Frage suchen"
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-custom-yellow"
            />
            <img
              src={SEARCH}
              alt="search"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
            />
          </div>
          {isSuperAdmin && (
            <button
              onClick={() => setIsModalOpen(true)}
              className='flex w-[250px] items-center font-bold rounded-lg px-4 py-2 bg-custom-yellow hover:bg-yellow-500 transition-colors shadow-sm'
            >
              <img src={ADD} alt="add" />
              <p className='text-white ml-2'>Frage hinzufügen</p>
            </button>
          )}
        </div>
        {/* List Container */}
        <div className="mt-4">
          <div className="grid grid-cols-12 text-sm font-medium text-gray-500 py-2 border-b mb-4">
            <div className="col-span-1">#</div>
            <div className="col-span-4">Titel der Frage</div>
            <div className="col-span-6">Frage</div>
            <div className="col-span-1"></div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-8 h-8 animate-spin text-custom-yellow" />
            </div>
          ) : filteredQuestions.length === 0 ? (
            <NotFoundErro imageUrl={constants.chips} title={'Sie haben keine Fragen hinzugefügt'} message={'Es scheint, dass Sie noch keine Fragen erstellt haben. Klicken Sie auf den obigen Button, um eine hinzuzufügen.'} />
          ) : (
            <div className="relative">
              {reorderLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-60">
                  <Loader size="lg" color="gray" />
                </div>
              )}
              {isSuperAdmin ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="sq-list">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {filteredQuestions.map((question, index) => (
                          <Draggable key={question.id} draggableId={String(question.id)} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="px-4 grid grid-cols-12 items-center py-2 bg-custom-gray_white hover:bg-gray-50 rounded-lg cursor-pointer mb-3"
                                onClick={() => handleQuestionClick(question)}
                              >
                                <div className="col-span-1 text-gray-500">{index + 1}</div>
                                <div className="col-span-4 font-medium">{truncateText(question.title, 50)}</div>
                                <div className="col-span-6 text-gray-600 truncate" title={question.description}>
                                  {truncateText(question.description, 30)}
                                </div>
                                <div className="col-span-1">
                                  {isSuperAdmin && (
                                    <button
                                      onClick={(e) => handleDelete(e, question.id)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <Trash2Icon size={20} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                // Static list for non-admins
                <div>
                  {filteredQuestions.map((question, index) => (
                    <div
                      key={question.id}
                      className="px-4 grid grid-cols-12 items-center py-2 bg-custom-gray_white rounded-lg mb-3"
                      onClick={() => handleQuestionClick(question)}
                    >
                      <div className="col-span-1 text-gray-500">{index + 1}</div>
                      <div className="col-span-4 font-medium">{truncateText(question.title, 50)}</div>
                      <div className="col-span-6 text-gray-600 truncate" title={question.description}>
                        {truncateText(question.description, 30)}
                      </div>
                      <div className="col-span-1"></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      <AddQuestionModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedQuestion={selectedQuestion}
        onSubmit={handleAddQuestion}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setQuestionToDelete(null);
        }}
        onDelete={confirmDelete}
        title="Standardfrage löschen"
        message="Sind Sie sicher, dass Sie diese Standardfrage löschen möchten?"
      />
    </div>
  )
}

export default StandarQustions